import {
  previousRewardPeriod,
  REWARD_ACTIONS,
  REWARD_AWARD_STATUSES,
  REWARD_NOMINATION_KINDS,
  REWARD_PERIOD_KINDS,
  type RewardAction,
  type RewardAward,
  type RewardNomination,
  type RewardPeriodSummary,
  type RewardRules,
  type RewardRulesView,
  type RewardStandingDetail,
  type RewardsView,
  SUGGESTION_STATUSES,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Award, ChevronDown, ChevronUp, FileSpreadsheet, Plus, Settings2, Trophy, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date, dateTime, plural, tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const ACTIONS = Object.keys(REWARD_ACTIONS) as RewardAction[];
/** Коротко для строки участника: «подано 2 · в план 1 · выпущено 1 · голосов 3 · ошибок 1» */
const SHORT: Record<RewardAction, string> = {
  submitted: 'подано',
  accepted: 'в план',
  released: 'выпущено',
  vote: 'голосов',
  bug: 'ошибок',
};
const AWARD_TONE = { approved: 'accent', paid: 'success', cancelled: 'neutral' } as const;

const pts = (n: number) => plural(n, 'балл', 'балла', 'баллов');
/** Ссылки и кнопки в тексте: на телефоне не ниже 44 px, на компьютере — как текст */
const TAP = 'inline-flex min-h-11 items-center md:min-h-0';

/**
 * Сумма из поля: «50 000», «50000,5», «50 000 ₸» — число; пусто — null; иначе undefined — поле с ошибкой.
 * «50,000» и «1.000.000» не угадываются: запятая и точка — только перед копейками
 */
function parseAmount(text: string): number | null | undefined {
  const t = text.replace(/[\s\u00A0\u202F]/g, '').replace(/(₸|тг\.?|тенге)$/i, '');
  if (t === '') return null;
  if (!/^\d{1,10}(?:[.,]\d{1,2})?$/.test(t)) return undefined;
  const n = Number(t.replace(',', '.'));
  return n <= 1_000_000_000 ? n : undefined;
}
const AMOUNT_ERROR = 'Сумма — числом, например 50 000 или 50 000,50';
const REASON_MIN = 5;

/** После решений по премиям обновляется всё связанное: вкладки, карточки предложений и «Ждёт меня» */
function useRefreshRewards() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['support'] });
    void qc.invalidateQueries({ queryKey: ['inbox'] });
  };
}

/** Кому утверждающий не назначает премию: себе и тем, кого сейчас замещает */
function useBlocked() {
  const me = useMe();
  return useMemo(
    () => new Set([me.user.id, ...(me.actingFor ?? []).map((a) => a.userId)]),
    [me.user.id, me.actingFor],
  );
}

export const rewardsQuery = (period?: string) => ({
  queryKey: ['support', 'rewards', period ?? ''],
  queryFn: () => api<RewardsView>('/support/rewards', { query: { period } }),
});

/**
 * Премии за предложения и активность (БЗ-34, БЗ-35). Каждый видит, как начисляются баллы, свои баллы и место,
 * из каких записей они сложились, и лучших прошлых периодов без сумм. Специалисты поддержки видят всех участников
 * и лучшие предложения периода; специалист с уровнем «Все деньги» задаёт правила и суммы, утверждает итоги,
 * отмечает выплату и выгружает ведомость.
 */
export function RewardsPanel({ period, onPeriod }: { period?: string; onPeriod: (p?: string) => void }) {
  const qc = useQueryClient();
  const q = useQuery(rewardsQuery(period));
  const [dialog, setDialog] = useState<'rules' | 'close' | 'add' | null>(null);
  const v = q.data;

  // Своя новая премия на экране — значит, человек её увидел: она уходит из «Ждёт меня»
  const unseen = useMemo(() => {
    if (!v) return [];
    const all = [...(v.period.closedPeriod?.awards ?? []), ...v.history.flatMap((h) => h.awards)];
    return [...new Set(all.filter((a) => a.unseen).map((a) => a.id))];
  }, [v]);
  useEffect(() => {
    if (!unseen.length) return;
    Promise.all(unseen.map((id) => api(`/support/rewards/awards/${id}/seen`, { method: 'POST' })))
      .then(() => {
        void qc.invalidateQueries({ queryKey: ['inbox'] });
        void qc.invalidateQueries({ queryKey: ['support', 'rewards'] });
      })
      .catch(() => {});
  }, [unseen, qc]);

  if (q.isPending) return <Spinner />;
  if (q.isError || !v)
    return (
      <div className="flex flex-col gap-3">
        <ErrorBox error={q.error} />
        {period ? (
          <div>
            <Button onClick={() => onPeriod(undefined)}>К текущему периоду</Button>
          </div>
        ) : null}
      </div>
    );

  // Периоды по убыванию: текущий, прошлый (свои баллы до утверждения итогов видит каждый), неутверждённые, итоги
  const options = new Map<string, string>();
  options.set(v.current.start, `Сейчас: ${v.current.label}`);
  for (const h of v.history) options.set(h.start, `${h.label} — итоги утверждены`);
  for (const p of v.pending) options.set(p.start, `${p.label} — итоги не подведены`);
  const last = previousRewardPeriod(v.rules.period, v.current.start);
  if (!options.has(last.start)) options.set(last.start, `${last.label} — итоги ещё не подведены`);
  if (!options.has(v.period.start)) options.set(v.period.start, v.period.label);
  const periods = [...options].sort((a, b) => b[0].localeCompare(a[0]));
  const canClose = v.canApprove && v.rules.enabled && v.period.ended && !v.period.closed;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full max-w-xs">
          <Select
            className="h-11 md:h-10"
            aria-label="Период премирования"
            value={v.period.start}
            onChange={(e) => onPeriod(e.target.value === v.current.start ? undefined : e.target.value)}
          >
            {periods.map(([start, label]) => (
              <option key={start} value={start}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        {canClose ? (
          <Button
            variant="primary"
            icon={<Trophy className="size-4" aria-hidden />}
            onClick={() => setDialog('close')}
          >
            Подвести итоги
          </Button>
        ) : null}
        {v.canApprove && v.period.closedPeriod ? (
          <>
            <Button icon={<Plus className="size-4" aria-hidden />} onClick={() => setDialog('add')}>
              Добавить премию
            </Button>
            <a
              href={`/api/v1/support/rewards/periods/${v.period.closedPeriod.id}/sheet.xlsx`}
              className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline md:h-10"
            >
              <FileSpreadsheet className="size-4" aria-hidden />
              Ведомость (Excel)
            </a>
          </>
        ) : null}
        {v.canApprove ? (
          <Button
            variant="ghost"
            icon={<Settings2 className="size-4" aria-hidden />}
            onClick={() => setDialog('rules')}
          >
            Правила
          </Button>
        ) : null}
      </div>

      {!v.rules.enabled ? (
        <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-[14px]">
          Премирование выключено.{' '}
          {v.canApprove
            ? 'Включите его в правилах: баллы уже считаются, итоги можно будет подвести за прошедший период.'
            : 'Его включает владелец портала.'}
        </div>
      ) : null}
      {v.canManage && v.pending.length ? (
        <div className="rounded-lg border border-warning/50 bg-warning-soft px-4 py-3 text-[14px] text-warning">
          Итоги не подведены:{' '}
          {v.pending.map((p, i) => (
            <span key={p.start}>
              {i ? ', ' : ''}
              <button
                type="button"
                className={`${TAP} font-semibold underline underline-offset-2`}
                onClick={() => onPeriod(p.start)}
              >
                {p.label}
              </button>
            </span>
          ))}
          .{' '}
          {v.canApprove
            ? 'Выберите лучших и утвердите итоги.'
            : 'Их утверждает специалист с доступом ко всем деньгам.'}
        </div>
      ) : null}
      {v.period.closedPeriod ? (
        <div className="rounded-lg border border-line bg-surface px-4 py-3 text-[14px]">
          Итоги за {v.period.label} утверждены {dateTime(v.period.closedPeriod.closedAt)}
          {v.period.closedPeriod.closedByName ? `, ${v.period.closedPeriod.closedByName}` : ''}. Баллы — на
          момент утверждения и больше не пересчитываются.
          {v.period.closedPeriod.note ? (
            <span className="block text-ink-2">{v.period.closedPeriod.note}</span>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <MyPoints view={v} />
          {v.period.closedPeriod ? <PeriodAwards view={v} summary={v.period.closedPeriod} /> : null}
          {v.standings ? <Standings view={v} /> : null}
          {v.candidates ? <Candidates view={v} /> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <RulesInfo rules={v.rules} canApprove={v.canApprove} />
          <HallOfFame history={v.history} onPeriod={onPeriod} />
        </div>
      </div>

      {dialog === 'rules' ? <RulesDialog rules={v.rules} onClose={() => setDialog(null)} /> : null}
      {dialog === 'close' ? <CloseDialog view={v} onClose={() => setDialog(null)} /> : null}
      {dialog === 'add' && v.period.closedPeriod ? (
        <AddAwardDialog view={v} summary={v.period.closedPeriod} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function MyPoints({ view: v }: { view: RewardsView }) {
  const me = v.me;
  return (
    <Panel
      title={`Мои баллы за ${v.period.label}`}
      meta={
        v.period.closed
          ? 'итоги утверждены'
          : v.period.ended
            ? 'период закончился'
            : `период ${date(v.period.start)} — ${date(v.period.end)}`
      }
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="num text-[32px] leading-10 font-semibold">{me.points}</span>
        <span className="text-[14px] text-ink-2">
          {me.place ? `${me.place}-е место из ${me.of}` : 'баллов пока нет'}
        </span>
      </div>
      {me.lines.length ? (
        <ul className="mt-3 flex flex-col divide-y divide-line">
          {me.lines.map((l) => (
            <li key={`${l.path}|${l.action}|${l.at}`} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="text-[12px] text-ink-2">
                  {date(l.at)} · {REWARD_ACTIONS[l.action]}
                </div>
                <Link to={l.path} className={`${TAP} text-[14px] [overflow-wrap:anywhere] hover:underline`}>
                  {l.title}
                </Link>
              </div>
              <span className="num shrink-0 text-[14px] font-semibold">+{l.points}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[14px] text-ink-2">
          Предложите, что сделать удобнее, кнопкой «Сообщить» → «Доработка», поддерживайте полезные
          предложения коллег и сообщайте об ошибках — баллы начисляются сами.
        </p>
      )}
    </Panel>
  );
}

function StandingRow({
  view: v,
  s,
}: {
  view: RewardsView;
  s: NonNullable<RewardsView['standings']>[number];
}) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['support', 'rewards', 'standing', s.userId, v.period.start],
    queryFn: () =>
      api<RewardStandingDetail>(`/support/rewards/standing/${s.userId}`, {
        query: { period: v.period.start },
      }),
    enabled: open,
  });
  const counts = ACTIONS.filter((a) => s.counts[a]).map((a) => `${SHORT[a]} ${s.counts[a]}`);
  return (
    <li className="px-5 py-2.5">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center gap-3 text-left md:min-h-9"
      >
        <span className="num w-7 shrink-0 text-[14px] text-ink-2">{s.place}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-medium [overflow-wrap:anywhere]">{s.fullName}</span>
          <span className="block text-[12px] text-ink-2">{counts.join(' · ')}</span>
        </span>
        <span className="num shrink-0 text-[14px] font-semibold">{pts(s.points)}</span>
        {open ? (
          <ChevronUp className="size-4 shrink-0 text-ink-2" aria-hidden />
        ) : (
          <ChevronDown className="size-4 shrink-0 text-ink-2" aria-hidden />
        )}
      </button>
      {open ? (
        detail.isPending ? (
          <Spinner />
        ) : detail.data ? (
          <ul className="mt-1 mb-1 ml-10 flex flex-col gap-1">
            {detail.data.lines.map((l) => (
              <li key={`${l.path}|${l.action}|${l.at}`} className="text-[13px]">
                <span className="text-ink-2">
                  {date(l.at)} · {REWARD_ACTIONS[l.action]} · +{l.points} ·{' '}
                </span>
                <Link to={l.path} className={`${TAP} [overflow-wrap:anywhere] hover:underline`}>
                  {l.title}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <ErrorBox error={detail.error} />
        )
      ) : null}
    </li>
  );
}

function Standings({ view: v }: { view: RewardsView }) {
  const list = v.standings ?? [];
  return (
    <Panel
      title="Участники"
      meta="Видят специалисты поддержки: остальные — только свои баллы и место"
      bodyClassName="p-0"
    >
      {list.length === 0 ? (
        <p className="px-5 py-4 text-[14px] text-ink-2">За период баллов ни у кого нет.</p>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((s) => (
            <StandingRow key={s.userId} view={v} s={s} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Candidates({ view: v }: { view: RewardsView }) {
  const list = v.candidates ?? [];
  return (
    <Panel
      title="Лучшие предложения периода"
      meta="Выпущенные — первыми, затем по пользе, приоритету и голосам"
      bodyClassName="p-0"
    >
      {list.length === 0 ? (
        <p className="px-5 py-4 text-[14px] text-ink-2">За период предложений нет.</p>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((c) => (
            <li key={c.suggestionId} className="flex flex-col gap-1 px-5 py-3">
              <div className="flex flex-wrap items-center gap-2">
                {c.releasedInPeriod ? <Badge tone="success">Выпущено в периоде</Badge> : null}
                <Badge tone="neutral">{SUGGESTION_STATUSES[c.status]}</Badge>
              </div>
              <Link
                to="/support/suggestions/$id"
                params={{ id: c.suggestionId }}
                className={`${TAP} text-[14px] font-medium [overflow-wrap:anywhere] hover:underline`}
              >
                {c.number} · {c.title}
              </Link>
              <span className="text-[12px] text-ink-2">
                {c.authorName} · польза {c.benefit ?? 'не оценена'} · голосов {c.votes} · приоритет{' '}
                {c.priority}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function AwardLine({ a, actions }: { a: RewardAward; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Award className="size-4 text-accent-text" aria-hidden />
        <span className="text-[14px] font-semibold">{a.nomination}</span>
        <Badge tone={AWARD_TONE[a.status]}>{REWARD_AWARD_STATUSES[a.status]}</Badge>
        {a.mine ? <Badge tone="accent">Ваша премия</Badge> : null}
      </div>
      <div className="text-[14px] [overflow-wrap:anywhere]">
        {a.fullName}
        {a.points !== null ? <span className="whitespace-nowrap"> · {pts(a.points)}</span> : null}
        {a.amount !== null ? <span className="whitespace-nowrap"> · {tenge(a.amount)}</span> : null}
      </div>
      {a.suggestion ? (
        <Link
          to="/support/suggestions/$id"
          params={{ id: a.suggestion.id }}
          className={`${TAP} self-start text-[14px] [overflow-wrap:anywhere] hover:underline`}
        >
          {a.suggestion.number} · {a.suggestion.title}
        </Link>
      ) : null}
      <p className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{a.reason}</p>
      {a.status === 'cancelled' && a.cancelReason ? (
        <p className="text-[13px] text-danger [overflow-wrap:anywhere]">Отменена: {a.cancelReason}</p>
      ) : null}
      {a.paidAt ? <p className="text-[12px] text-ink-2">Выплачена {date(a.paidAt)}</p> : null}
      {actions}
    </div>
  );
}

/** Премии утверждённого периода: выплата и отмена — у того, кто утверждает */
function PeriodAwards({ view: v, summary }: { view: RewardsView; summary: RewardPeriodSummary }) {
  const qc = useQueryClient();
  const refresh = useRefreshRewards();
  const [cancel, setCancel] = useState<RewardAward | null>(null);
  const [pay, setPay] = useState<RewardAward | null>(null);
  const [reason, setReason] = useState('');
  const done = (d: RewardsView) => {
    qc.setQueryData(
      rewardsQuery(v.period.start === v.current.start ? undefined : v.period.start).queryKey,
      d,
    );
    refresh();
  };
  const paid = useMutation({
    mutationFn: (a: RewardAward) =>
      api<RewardsView>(`/support/rewards/awards/${a.id}/paid`, { method: 'POST' }),
    onSuccess: (d) => {
      toast('Выплата отмечена');
      setPay(null);
      done(d);
    },
  });
  const drop = useMutation({
    mutationFn: (a: RewardAward) =>
      api<RewardsView>(`/support/rewards/awards/${a.id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: (d) => {
      toast('Премия отменена, получатель увидит причину');
      closeCancel();
      done(d);
    },
  });
  // Каждая премия — с чистой формой: причина и ошибка прошлой отмены к другой премии не переходят
  const openCancel = (a: RewardAward) => {
    setReason('');
    drop.reset();
    setCancel(a);
  };
  const closeCancel = () => {
    setCancel(null);
    setReason('');
    drop.reset();
  };
  const openPay = (a: RewardAward) => {
    paid.reset();
    setPay(a);
  };
  return (
    <Panel title={`Премии за ${summary.label}`} bodyClassName="p-0">
      {summary.awards.length === 0 ? (
        <p className="px-5 py-4 text-[14px] text-ink-2">Премий за период нет.</p>
      ) : (
        <ul className="divide-y divide-line">
          {summary.awards.map((a) => (
            <li key={a.id} className="px-5 py-3">
              <AwardLine
                a={a}
                actions={
                  v.canApprove && a.status === 'approved' ? (
                    <div className="mt-1 flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => openPay(a)}>
                        Отметить выплату
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openCancel(a)}>
                        Отменить
                      </Button>
                    </div>
                  ) : null
                }
              />
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={Boolean(pay)}
        onOpenChange={(o) => (o ? null : setPay(null))}
        title="Отметить выплату"
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPay(null)}>
              Не сейчас
            </Button>
            <Button variant="primary" loading={paid.isPending} onClick={() => pay && paid.mutate(pay)}>
              Отметить выплату
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {pay ? (
            <p className="text-[14px] leading-6">
              {pay.nomination} · {pay.fullName}
              {pay.amount !== null ? ` · ${tenge(pay.amount)}` : ''}. Снять отметку о выплате нельзя: после
              неё премию уже не отменить.
            </p>
          ) : null}
          {paid.isError ? <ErrorBox error={paid.error} /> : null}
        </div>
      </Dialog>
      <Dialog
        open={Boolean(cancel)}
        onOpenChange={(o) => (o ? null : closeCancel())}
        title="Отменить премию"
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={closeCancel}>
              Не отменять
            </Button>
            <Button
              variant="danger"
              loading={drop.isPending}
              disabled={reason.trim().length < REASON_MIN}
              onClick={() => cancel && drop.mutate(cancel)}
            >
              Отменить премию
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {cancel ? (
            <p className="text-[14px]">
              {cancel.nomination} · {cancel.fullName}. Получатель увидит причину отмены.
            </p>
          ) : null}
          <Field label="Причина" htmlFor="rw-cancel" hint="Не короче 5 знаков">
            <Textarea
              id="rw-cancel"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </Field>
          {drop.isError ? <ErrorBox error={drop.error} /> : null}
        </div>
      </Dialog>
    </Panel>
  );
}

function RulesInfo({ rules, canApprove }: { rules: RewardRulesView; canApprove: boolean }) {
  return (
    <Panel title="Как начисляются баллы" meta={`период — ${REWARD_PERIOD_KINDS[rules.period].toLowerCase()}`}>
      <ul className="flex flex-col gap-1.5 text-[14px]">
        {ACTIONS.map((a) => (
          <li key={a} className="flex items-baseline justify-between gap-3">
            <span>{REWARD_ACTIONS[a]}</span>
            <span className="num shrink-0 font-semibold">+{rules.points[a]}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[13px] text-ink-2">
        Голосов коллег одному предложению засчитывается не больше {rules.voteCap} за период; свой голос и
        повторы баллов не дают. Баллы считаются только по записям портала.
      </p>
      <h3 className="mt-4 text-[14px] font-semibold">Номинации</h3>
      <ul className="mt-1 flex flex-col gap-1.5 text-[14px]">
        {rules.nominations.map((n) => (
          <li key={n.code} className="flex flex-col">
            <span className="flex items-baseline justify-between gap-3">
              <span className="font-medium">{n.title}</span>
              {n.amount !== null ? (
                <span className="num shrink-0 whitespace-nowrap">{tenge(n.amount)}</span>
              ) : null}
            </span>
            <span className="text-[12px] text-ink-2">
              {REWARD_NOMINATION_KINDS[n.kind].toLowerCase()}, победителей: {n.winners}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[13px] text-ink-2">
        Победителей выбирает человек после конца периода, портал только подсказывает кандидатов.
        {rules.amountsHidden || !canApprove ? ' Суммы видит тот, кто утверждает премии.' : ''}
      </p>
    </Panel>
  );
}

/** Доска лучших: утверждённые итоги последних периодов — видят все сотрудники, без сумм */
function HallOfFame({
  history,
  onPeriod,
}: {
  history: RewardPeriodSummary[];
  onPeriod: (p?: string) => void;
}) {
  const shown = history.filter((h) => h.awards.some((a) => a.status !== 'cancelled'));
  return (
    <Panel title="Лучшие предложения и участники">
      {shown.length === 0 ? (
        <EmptyState title="Итогов пока нет">
          Первые победители появятся здесь после утверждения итогов месяца.
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          {shown.map((h) => (
            <section key={h.id} className="flex flex-col gap-2">
              <button
                type="button"
                className="min-h-11 self-start text-left text-[14px] font-semibold underline-offset-2 hover:underline md:min-h-0"
                onClick={() => onPeriod(h.start)}
              >
                {h.label}
              </button>
              {h.awards
                .filter((a) => a.status !== 'cancelled')
                .map((a) => (
                  <AwardLine key={a.id} a={a} />
                ))}
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
}

/* ───────────────────────── Подвести итоги ───────────────────────── */

interface Slot {
  key: string;
  nomination: RewardNomination;
  userId: string;
  suggestionId: string | null;
  amount: string;
  reason: string;
}

function CloseDialog({ view: v, onClose }: { view: RewardsView; onClose: () => void }) {
  const qc = useQueryClient();
  const refresh = useRefreshRewards();
  const blocked = useBlocked();
  const [note, setNote] = useState('');
  const [slots, setSlots] = useState<Slot[]>(() => {
    const out: Slot[] = [];
    const candidates = (v.candidates ?? []).filter((c) => !blocked.has(c.authorId));
    const standings = (v.standings ?? []).filter((s) => !blocked.has(s.userId));
    for (const n of v.rules.nominations)
      for (let i = 0; i < n.winners; i++) {
        const c = n.kind === 'suggestion' ? candidates[i] : undefined;
        const s = n.kind === 'activity' ? standings[i] : undefined;
        out.push({
          key: `${n.code}-${i}`,
          nomination: n,
          userId: c?.authorId ?? s?.userId ?? '',
          suggestionId: c?.suggestionId ?? null,
          amount: n.amount === null ? '' : String(n.amount),
          reason: c
            ? `${c.number} «${c.title}»: ${SUGGESTION_STATUSES[c.status].toLowerCase()}, польза ${c.benefit ?? 'не оценена'}, голосов ${c.votes}`
            : s
              ? `${s.place === 1 ? 'Больше всех баллов' : `${s.place}-е место по баллам`} за ${v.period.label}: ${s.points}`
              : '',
        });
      }
    return out;
  });
  const filled = slots.filter((x) => x.userId);
  // Ошибки по полям — до отправки, а не одно «Проверьте заполнение полей» на все места
  const local = (x: Slot) => ({
    amount: parseAmount(x.amount) === undefined ? AMOUNT_ERROR : undefined,
    reason:
      x.reason.trim().length < REASON_MIN
        ? 'Напишите, за что премия: не короче 5 знаков, это увидит победитель'
        : undefined,
  });
  const invalid = filled.some((x) => local(x).amount || local(x).reason);
  const save = useMutation({
    mutationFn: () =>
      api<RewardsView>('/support/rewards/periods', {
        method: 'POST',
        body: {
          start: v.period.start,
          note: note.trim() || null,
          awards: filled.map((x) => ({
            nomination: x.nomination.code,
            userId: x.userId,
            suggestionId: x.nomination.kind === 'suggestion' ? x.suggestionId : null,
            amount: parseAmount(x.amount) ?? null,
            reason: x.reason,
          })),
        },
      }),
    onSuccess: (d) => {
      toast(`Итоги за ${v.period.label} утверждены, победители получат уведомление`);
      qc.setQueryData(rewardsQuery(v.period.start).queryKey, d);
      refresh();
      onClose();
    },
  });
  const set = (key: string, patch: Partial<Slot>) => {
    save.reset();
    setSlots((list) => list.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  };
  // Ошибки сервера по местам: awards.N — N-е заполненное место
  const server = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const serverOf = (x: Slot, field: 'amount' | 'reason' | 'userId') => {
    const i = filled.findIndex((y) => y.key === x.key);
    return i < 0 ? undefined : server[`awards.${i}.${field}`];
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={`Итоги за ${v.period.label}`}
      description="Портал подставил кандидатов по баллам и пользе — решение за вами. После утверждения итоги и баллы фиксируются."
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={invalid} onClick={() => save.mutate()}>
            Утвердить итоги
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {slots.map((x) => {
          const err = x.userId ? local(x) : { amount: undefined, reason: undefined };
          return (
            <fieldset key={x.key} className="flex flex-col gap-3 rounded-md border border-line p-3">
              <legend className="px-1 text-[14px] font-semibold">
                {x.nomination.title}
                {x.nomination.winners > 1 ? ` — место ${Number(x.key.split('-').pop()) + 1}` : ''}
              </legend>
              {x.nomination.kind === 'suggestion' ? (
                <Field label="Предложение" htmlFor={`rw-s-${x.key}`} error={serverOf(x, 'userId')}>
                  <Select
                    id={`rw-s-${x.key}`}
                    className="h-11 md:h-10"
                    value={x.suggestionId ?? ''}
                    onChange={(e) => {
                      const c = (v.candidates ?? []).find((y) => y.suggestionId === e.target.value);
                      set(x.key, {
                        suggestionId: c?.suggestionId ?? null,
                        userId: c?.authorId ?? '',
                        reason: c
                          ? `${c.number} «${c.title}»: ${SUGGESTION_STATUSES[c.status].toLowerCase()}, польза ${c.benefit ?? 'не оценена'}, голосов ${c.votes}`
                          : '',
                      });
                    }}
                  >
                    <option value="">Без премии в этой номинации</option>
                    {(v.candidates ?? []).map((c) => (
                      <option key={c.suggestionId} value={c.suggestionId} disabled={blocked.has(c.authorId)}>
                        {c.number} {c.title} — {c.authorName}
                        {blocked.has(c.authorId) ? ' (вам нельзя)' : ''}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Field label="Сотрудник" htmlFor={`rw-u-${x.key}`} error={serverOf(x, 'userId')}>
                  <Select
                    id={`rw-u-${x.key}`}
                    className="h-11 md:h-10"
                    value={x.userId}
                    onChange={(e) => {
                      const s = (v.standings ?? []).find((y) => y.userId === e.target.value);
                      set(x.key, {
                        userId: s?.userId ?? '',
                        reason: s ? `${s.place}-е место по баллам за ${v.period.label}: ${s.points}` : '',
                      });
                    }}
                  >
                    <option value="">Без премии в этой номинации</option>
                    {(v.standings ?? []).map((s) => (
                      <option key={s.userId} value={s.userId} disabled={blocked.has(s.userId)}>
                        {s.place}. {s.fullName} — {pts(s.points)}
                        {blocked.has(s.userId) ? ' (вам нельзя)' : ''}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {x.userId ? (
                <div className="grid gap-3 md:grid-cols-[200px_minmax(0,1fr)]">
                  <Field
                    label="Сумма"
                    htmlFor={`rw-a-${x.key}`}
                    hint="Пусто — без суммы"
                    error={err.amount ?? serverOf(x, 'amount')}
                  >
                    <Input
                      id={`rw-a-${x.key}`}
                      className="h-11 md:h-10"
                      inputMode="decimal"
                      value={x.amount}
                      aria-invalid={Boolean(err.amount)}
                      onChange={(e) => set(x.key, { amount: e.target.value })}
                    />
                  </Field>
                  <Field
                    label="За что — увидит победитель"
                    htmlFor={`rw-r-${x.key}`}
                    error={err.reason ?? serverOf(x, 'reason')}
                  >
                    <Textarea
                      id={`rw-r-${x.key}`}
                      rows={2}
                      value={x.reason}
                      maxLength={500}
                      aria-invalid={Boolean(err.reason)}
                      onChange={(e) => set(x.key, { reason: e.target.value })}
                    />
                  </Field>
                </div>
              ) : null}
            </fieldset>
          );
        })}
        <Field label="Пояснение к итогам" htmlFor="rw-note">
          <Textarea
            id="rw-note"
            rows={2}
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}

/**
 * Добавить премию к утверждённым итогам: по правилам и баллам на момент утверждения — номинации и суммы из правил,
 * сохранённых с итогами, а не из нынешних (их могли поменять после утверждения)
 */
function AddAwardDialog({
  view: v,
  summary,
  onClose,
}: {
  view: RewardsView;
  summary: RewardPeriodSummary;
  onClose: () => void;
}) {
  const refresh = useRefreshRewards();
  const blocked = useBlocked();
  const nominations = summary.nominations ?? v.rules.nominations;
  const [code, setCode] = useState(nominations[0]?.code ?? '');
  const n = nominations.find((x) => x.code === code);
  const [userId, setUserId] = useState('');
  const [suggestionId, setSuggestionId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const amountValue = parseAmount(amount);
  const amountError = amountValue === undefined ? AMOUNT_ERROR : undefined;
  const save = useMutation({
    mutationFn: () =>
      api<RewardsView>(`/support/rewards/periods/${summary.id}/awards`, {
        method: 'POST',
        body: {
          nomination: code,
          userId,
          suggestionId: n?.kind === 'suggestion' ? suggestionId || null : null,
          ...(amountValue === null || amountValue === undefined ? {} : { amount: amountValue }),
          reason,
        },
      }),
    onSuccess: () => {
      toast('Премия добавлена, получатель получит уведомление');
      refresh();
      onClose();
    },
  });
  const server = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={`Добавить премию за ${v.period.label}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!userId || reason.trim().length < REASON_MIN || Boolean(amountError)}
            onClick={() => save.mutate()}
          >
            Добавить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Номинация" htmlFor="rw-add-n" hint="Из правил на момент утверждения итогов">
          <Select
            id="rw-add-n"
            className="h-11 md:h-10"
            value={code}
            onChange={(e) => {
              // Другая номинация — выбор заново: за предложение и за активность выбираются по-разному
              setCode(e.target.value);
              setUserId('');
              setSuggestionId('');
              setAmount('');
              save.reset();
            }}
          >
            {nominations.map((x) => (
              <option key={x.code} value={x.code}>
                {x.title}
              </option>
            ))}
          </Select>
        </Field>
        {n?.kind === 'suggestion' ? (
          <Field label="Предложение" htmlFor="rw-add-s" error={server.suggestionId ?? server.userId}>
            <Select
              id="rw-add-s"
              className="h-11 md:h-10"
              value={suggestionId}
              onChange={(e) => {
                const c = (v.candidates ?? []).find((y) => y.suggestionId === e.target.value);
                setSuggestionId(c?.suggestionId ?? '');
                setUserId(c?.authorId ?? '');
              }}
            >
              <option value="">Выберите</option>
              {(v.candidates ?? []).map((c) => (
                <option key={c.suggestionId} value={c.suggestionId} disabled={blocked.has(c.authorId)}>
                  {c.number} {c.title} — {c.authorName}
                  {blocked.has(c.authorId) ? ' (вам нельзя)' : ''}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field
            label="Сотрудник"
            htmlFor="rw-add-u"
            hint="Участники с баллами на момент утверждения итогов"
            error={server.userId}
          >
            <Select
              id="rw-add-u"
              className="h-11 md:h-10"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Выберите</option>
              {(v.standings ?? []).map((s) => (
                <option key={s.userId} value={s.userId} disabled={blocked.has(s.userId)}>
                  {s.place}. {s.fullName} — {pts(s.points)}
                  {blocked.has(s.userId) ? ' (вам нельзя)' : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field
          label="Сумма"
          htmlFor="rw-add-a"
          error={amountError ?? server.amount}
          hint={
            n?.amount !== null && n?.amount !== undefined
              ? `Пусто — по правилам итогов: ${tenge(n.amount)}`
              : 'Пусто — без суммы'
          }
        >
          <Input
            id="rw-add-a"
            className="h-11 md:h-10"
            inputMode="decimal"
            value={amount}
            aria-invalid={Boolean(amountError)}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field
          label="За что — увидит получатель"
          htmlFor="rw-add-r"
          hint="Не короче 5 знаков"
          error={server.reason}
        >
          <Textarea
            id="rw-add-r"
            rows={2}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Правила ───────────────────────── */

/** Номинация в форме: сумма — как набрана, число получается при сохранении */
type NominationDraft = RewardNomination & { amountText: string };

function RulesDialog({ rules, onClose }: { rules: RewardRulesView; onClose: () => void }) {
  const refresh = useRefreshRewards();
  const { amountsHidden: _hidden, nominations: initialNominations, ...initial } = rules;
  const [r, setR] = useState<Omit<RewardRules, 'nominations'>>(initial);
  const [noms, setNoms] = useState<NominationDraft[]>(() =>
    initialNominations.map((n) => ({ ...n, amountText: n.amount === null ? '' : String(n.amount) })),
  );
  const num = (s: string) => (s.trim() === '' ? 0 : Math.max(0, Math.round(Number(s) || 0)));
  const setN = (i: number, patch: Partial<NominationDraft>) =>
    setNoms((list) => list.map((y, j) => (j === i ? { ...y, ...patch } : y)));
  // Те же проверки, что на сервере: ошибка видна у своего поля до сохранения
  const reviewError = r.reviewDays < 3 || r.reviewDays > 90 ? 'От 3 до 90 дней' : undefined;
  const nomErrors = noms.map((n) => ({
    title: n.title.trim().length < 3 ? 'Назовите номинацию: не короче 3 знаков' : undefined,
    amount: parseAmount(n.amountText) === undefined ? AMOUNT_ERROR : undefined,
  }));
  const invalid = Boolean(reviewError) || nomErrors.some((e) => e.title || e.amount);
  const save = useMutation({
    mutationFn: () =>
      api<RewardRulesView>('/support/rewards/rules', {
        method: 'PUT',
        body: {
          ...r,
          nominations: noms.map(({ amountText, ...n }) => ({
            ...n,
            title: n.title.trim(),
            amount: parseAmount(amountText) ?? null,
          })),
        } satisfies RewardRules,
      }),
    onSuccess: () => {
      toast('Правила сохранены: изменения видны в истории настроек');
      // Срок разбора и «включено» меняют и анализ предложений, и «Ждёт меня»
      refresh();
      onClose();
    },
  });
  const server = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="Правила премирования"
      description="Утверждённые итоги не меняются: новые правила действуют для периодов без итогов"
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={invalid} onClick={() => save.mutate()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Checkbox
          label="Премирование включено"
          description="Выключенное: баллы считаются, но итоги не подводятся и напоминаний нет"
          checked={r.enabled}
          onChange={(e) => setR({ ...r, enabled: e.target.checked })}
        />
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Период" htmlFor="rw-period">
            <Select
              id="rw-period"
              className="h-11 md:h-10"
              value={r.period}
              onChange={(e) => setR({ ...r, period: e.target.value as RewardRules['period'] })}
            >
              <option value="month">Месяц</option>
              <option value="quarter">Квартал</option>
            </Select>
          </Field>
          <Field
            label="Голосов одному предложению"
            htmlFor="rw-cap"
            hint="Засчитывается за период"
            error={server.voteCap}
          >
            <Input
              id="rw-cap"
              className="h-11 md:h-10"
              inputMode="numeric"
              value={String(r.voteCap)}
              onChange={(e) => setR({ ...r, voteCap: Math.min(100, num(e.target.value)) })}
            />
          </Field>
          <Field
            label="Срок разбора, дней"
            htmlFor="rw-review"
            hint="Дольше — «ждёт решения»"
            error={reviewError ?? server.reviewDays}
          >
            <Input
              id="rw-review"
              className="h-11 md:h-10"
              inputMode="numeric"
              value={String(r.reviewDays)}
              aria-invalid={Boolean(reviewError)}
              onChange={(e) => setR({ ...r, reviewDays: Math.min(90, num(e.target.value)) })}
            />
          </Field>
        </div>
        <div>
          <h3 className="mb-2 text-[14px] font-semibold">Баллы за действия</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {ACTIONS.map((a) => (
              <Field key={a} label={REWARD_ACTIONS[a]} htmlFor={`rw-p-${a}`} error={server[`points.${a}`]}>
                <Input
                  id={`rw-p-${a}`}
                  className="h-11 md:h-10"
                  inputMode="numeric"
                  value={String(r.points[a])}
                  onChange={(e) =>
                    setR({ ...r, points: { ...r.points, [a]: Math.min(100, num(e.target.value)) } })
                  }
                />
              </Field>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-[14px] font-semibold">Номинации</h3>
          <div className="flex flex-col gap-3">
            {noms.map((n, i) => (
              <div
                key={n.code}
                className="grid gap-2 rounded-md border border-line p-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_100px_170px_auto] md:items-start"
              >
                <Field
                  label="Название"
                  htmlFor={`rw-nt-${i}`}
                  error={nomErrors[i]!.title ?? server[`nominations.${i}.title`]}
                >
                  <Input
                    id={`rw-nt-${i}`}
                    className="h-11 md:h-10"
                    value={n.title}
                    maxLength={100}
                    aria-invalid={Boolean(nomErrors[i]!.title)}
                    onChange={(e) => setN(i, { title: e.target.value })}
                  />
                </Field>
                <Field label="За что" htmlFor={`rw-nk-${i}`}>
                  <Select
                    id={`rw-nk-${i}`}
                    className="h-11 md:h-10"
                    value={n.kind}
                    onChange={(e) => setN(i, { kind: e.target.value as RewardNomination['kind'] })}
                  >
                    <option value="suggestion">За предложение</option>
                    <option value="activity">За активность</option>
                  </Select>
                </Field>
                <Field label="Победителей" htmlFor={`rw-nw-${i}`}>
                  <Select
                    id={`rw-nw-${i}`}
                    className="h-11 md:h-10"
                    value={n.winners}
                    onChange={(e) => setN(i, { winners: Number(e.target.value) })}
                  >
                    {[1, 2, 3, 4, 5].map((w) => (
                      <option key={w} value={w}>
                        {w}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Сумма"
                  htmlFor={`rw-na-${i}`}
                  hint="Пусто — без суммы"
                  error={nomErrors[i]!.amount ?? server[`nominations.${i}.amount`]}
                >
                  <Input
                    id={`rw-na-${i}`}
                    className="h-11 md:h-10"
                    inputMode="decimal"
                    value={n.amountText}
                    aria-invalid={Boolean(nomErrors[i]!.amount)}
                    onChange={(e) => setN(i, { amountText: e.target.value })}
                  />
                </Field>
                <Button
                  variant="ghost"
                  className="md:mt-6"
                  aria-label={`Убрать номинацию ${n.title}`}
                  disabled={noms.length <= 1}
                  onClick={() => setNoms((list) => list.filter((_, j) => j !== i))}
                >
                  <X className="size-4" aria-hidden />
                </Button>
              </div>
            ))}
          </div>
          {noms.length < 6 ? (
            <Button
              className="mt-2"
              size="sm"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() =>
                setNoms((list) => [
                  ...list,
                  {
                    code: `n${Date.now().toString(36)}`,
                    title: 'Новая номинация',
                    kind: 'suggestion',
                    amount: null,
                    amountText: '',
                    winners: 1,
                  },
                ])
              }
            >
              Добавить номинацию
            </Button>
          ) : null}
        </div>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}
